import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCode,
    faMagnifyingGlass,
    faServer,
    faClock,
    faRobot,
    faPaperPlane,
    faCheckCircle,
    faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import "./HomePage.css";

const SERVICE_KEYS = ["webDev", "webAudit", "itHardware", "aiSystems", "hourly"];
const SERVICE_ICONS = {
    webDev: faCode,
    webAudit: faMagnifyingGlass,
    itHardware: faServer,
    aiSystems: faRobot,
    hourly: faClock,
};

const ServiceCard = ({ serviceKey }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const bullets = t(`homepage.services.${serviceKey}.bullets`, { returnObjects: true });

    return (
        <div className={`service-card ${open ? "service-card--open" : ""}`}>
            <button
                className="service-card__header"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
            >
                <div className="service-card__icon">
                    <FontAwesomeIcon icon={SERVICE_ICONS[serviceKey]} />
                </div>
                <div className="service-card__summary">
                    <h3 className="service-card__title">
                        {t(`homepage.services.${serviceKey}.title`)}
                    </h3>
                    <p className="service-card__tagline">
                        {t(`homepage.services.${serviceKey}.tagline`)}
                    </p>
                </div>
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`service-card__chevron ${open ? "service-card__chevron--open" : ""}`}
                />
            </button>
            {open && (
                <ul className="service-card__bullets">
                    {Array.isArray(bullets) && bullets.map((b, i) => (
                        <li key={i}>
                            <FontAwesomeIcon icon={faCheckCircle} />
                            <span>{b}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const HomePage = () => {
    const { t } = useTranslation();

    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        service: "",
        timeline: "",
        message: "",
    });
    const [formState, setFormState] = useState("idle");
    const [formError, setFormError] = useState("");

    const handleChange = (e) =>
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormState("submitting");
        setFormError("");
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL || ""}/api/contact`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                    body: JSON.stringify(form),
                }
            );
            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.message || "Submission failed.");
            }
            setFormState("success");
            setForm({ name: "", email: "", company: "", service: "", timeline: "", message: "" });
        } catch (err) {
            setFormState("error");
            setFormError(err.message);
        }
    };

    return (
        <div className="homepage">

            {/* ── Hero ───────────────────────────────────────────── */}
            <section className="hero">
                <div className="hero__inner">
                    <p className="hero__eyebrow">{t("homepage.hero.eyebrow")}</p>
                    <h1 className="hero__title">
                        {t("homepage.hero.title1")}<br />
                        <span className="hero__title-accent">{t("homepage.hero.title2")}</span>
                    </h1>
                    <p className="hero__desc">{t("homepage.hero.desc")}</p>
                    <div className="hero__cta-row">
                        <a href="#contact" className="btn btn--primary">
                            {t("homepage.hero.ctaPrimary")}
                        </a>
                        <a href="#services" className="btn btn--ghost">
                            {t("homepage.hero.ctaSecondary")}
                        </a>
                    </div>
                    <div className="hero__ribbon" aria-hidden="true">
                        <span>React · Laravel · WordPress</span>
                        <span className="hero__ribbon-dot">·</span>
                        <span>IT Hardware</span>
                        <span className="hero__ribbon-dot">·</span>
                        <span>Web Audit</span>
                        <span className="hero__ribbon-dot">·</span>
                        <span>AI Systems</span>
                        <span className="hero__ribbon-dot">·</span>
                        <span>EN · FR · 普通話</span>
                    </div>
                </div>
            </section>

            {/* ── About ─────────────────────────────────────────── */}
            <section className="about">
                <div className="section-container">
                    <div className="about__layout">
                        <div className="about__text">
                            <h2 className="section-title">{t("homepage.about.title")}</h2>
                            <p>{t("homepage.about.p1")}</p>
                            <p>{t("homepage.about.p2")}</p>
                        </div>
                        <dl className="about__facts">
                            <div className="about__fact">
                                <dt>{t("homepage.about.expLabel")}</dt>
                                <dd>{t("homepage.about.expValue")}</dd>
                            </div>
                            <div className="about__fact">
                                <dt>{t("homepage.about.engLabel")}</dt>
                                <dd>{t("homepage.about.engValue")}</dd>
                            </div>
                            <div className="about__fact">
                                <dt>{t("homepage.about.basedLabel")}</dt>
                                <dd>{t("homepage.about.basedValue")}</dd>
                            </div>
                            <div className="about__fact">
                                <dt>{t("homepage.about.langLabel")}</dt>
                                <dd>{t("homepage.about.langValue")}</dd>
                            </div>
                        </dl>
                    </div>
                </div>
            </section>

            {/* ── Services ──────────────────────────────────────── */}
            <section id="services" className="services">
                <div className="section-container">
                    <div className="section-header">
                        <h2 className="section-title">{t("homepage.services.sectionTitle")}</h2>
                        <p className="section-subtitle">{t("homepage.services.sectionSubtitle")}</p>
                    </div>
                    <div className="services__list">
                        {SERVICE_KEYS.map((key) => (
                            <ServiceCard key={key} serviceKey={key} />
                        ))}
                    </div>
                </div>
            </section>

            {/* ── How it works ──────────────────────────────────── */}
            <section className="process">
                <div className="section-container">
                    <h2 className="section-title">{t("homepage.process.sectionTitle")}</h2>
                    <div className="process__steps">
                        <div className="process__step">
                            <span className="process__step-num">01</span>
                            <div>
                                <h3>{t("homepage.process.step1Title")}</h3>
                                <p>{t("homepage.process.step1Desc")}</p>
                            </div>
                        </div>
                        <div className="process__step">
                            <span className="process__step-num">02</span>
                            <div>
                                <h3>{t("homepage.process.step2Title")}</h3>
                                <p>{t("homepage.process.step2Desc")}</p>
                            </div>
                        </div>
                        <div className="process__step">
                            <span className="process__step-num">03</span>
                            <div>
                                <h3>{t("homepage.process.step3Title")}</h3>
                                <p>{t("homepage.process.step3Desc")}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Contact Form ──────────────────────────────────── */}
            <section id="contact" className="intake">
                <div className="section-container">
                    <div className="intake__layout">

                        <div className="intake__info">
                            <h2 className="section-title">{t("homepage.contact.sectionTitle")}</h2>
                            <p className="section-subtitle">{t("homepage.contact.sectionSubtitle")}</p>
                            <p className="intake__note">
                                {t("homepage.contact.portalNote")}{" "}
                                <Link to="/client-portal">
                                    {t("homepage.contact.portalLink")}
                                </Link>
                            </p>
                        </div>

                        <div className="intake__form-wrap">
                            {formState === "success" ? (
                                <div className="intake__success">
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                    <h3>{t("homepage.contact.successTitle")}</h3>
                                    <p>{t("homepage.contact.successDesc")}</p>
                                </div>
                            ) : (
                                <form className="intake__form" onSubmit={handleSubmit} noValidate>
                                    <div className="intake__row">
                                        <div className="form-group">
                                            <label htmlFor="f-name">
                                                {t("homepage.contact.nameLabel")} <span className="required">*</span>
                                            </label>
                                            <input
                                                id="f-name"
                                                name="name"
                                                type="text"
                                                autoComplete="name"
                                                required
                                                value={form.name}
                                                onChange={handleChange}
                                                placeholder={t("homepage.contact.namePlaceholder")}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="f-email">
                                                {t("homepage.contact.emailLabel")} <span className="required">*</span>
                                            </label>
                                            <input
                                                id="f-email"
                                                name="email"
                                                type="email"
                                                autoComplete="email"
                                                required
                                                value={form.email}
                                                onChange={handleChange}
                                                placeholder={t("homepage.contact.emailPlaceholder")}
                                            />
                                        </div>
                                    </div>

                                    <div className="intake__row">
                                        <div className="form-group">
                                            <label htmlFor="f-company">
                                                {t("homepage.contact.companyLabel")}
                                            </label>
                                            <input
                                                id="f-company"
                                                name="company"
                                                type="text"
                                                autoComplete="organization"
                                                value={form.company}
                                                onChange={handleChange}
                                                placeholder={t("homepage.contact.companyPlaceholder")}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="f-service">
                                                {t("homepage.contact.serviceLabel")} <span className="required">*</span>
                                            </label>
                                            <select
                                                id="f-service"
                                                name="service"
                                                required
                                                value={form.service}
                                                onChange={handleChange}
                                            >
                                                <option value="">—</option>
                                                <option value="Web Development">{t("homepage.contact.serviceWebDev")}</option>
                                                <option value="Web Audit">{t("homepage.contact.serviceWebAudit")}</option>
                                                <option value="IT Hardware Support">{t("homepage.contact.serviceItHardware")}</option>
                                                <option value="AI Training & Internal Systems">{t("homepage.contact.serviceAiSystems")}</option>
                                                <option value="Hourly Consulting">{t("homepage.contact.serviceHourly")}</option>
                                                <option value="Other">{t("homepage.contact.serviceOther")}</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="f-timeline">
                                            {t("homepage.contact.timelineLabel")}
                                        </label>
                                        <select
                                            id="f-timeline"
                                            name="timeline"
                                            value={form.timeline}
                                            onChange={handleChange}
                                        >
                                            <option value="">—</option>
                                            <option value="asap">{t("homepage.contact.timelineAsap")}</option>
                                            <option value="1-2w">{t("homepage.contact.timeline1_2w")}</option>
                                            <option value="1m">{t("homepage.contact.timeline1m")}</option>
                                            <option value="flexible">{t("homepage.contact.timelineFlexible")}</option>
                                        </select>
                                        <p className="form-helper">{t("homepage.contact.timelineHelper")}</p>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="f-message">
                                            {t("homepage.contact.messageLabel")} <span className="required">*</span>
                                        </label>
                                        <textarea
                                            id="f-message"
                                            name="message"
                                            required
                                            rows={6}
                                            value={form.message}
                                            onChange={handleChange}
                                            placeholder={t("homepage.contact.messagePlaceholder")}
                                        />
                                        <p className="form-helper">{t("homepage.contact.messageHelper")}</p>
                                    </div>

                                    {formState === "error" && (
                                        <div className="error-message">{formError}</div>
                                    )}

                                    <button
                                        type="submit"
                                        className="btn btn--primary intake__submit"
                                        disabled={formState === "submitting"}
                                    >
                                        <FontAwesomeIcon icon={faPaperPlane} />
                                        {formState === "submitting"
                                            ? t("homepage.contact.submittingBtn")
                                            : t("homepage.contact.submitBtn")}
                                    </button>
                                </form>
                            )}
                        </div>

                    </div>
                </div>
            </section>

            {/* ── Footer ────────────────────────────────────────── */}
            <footer className="site-footer">
                <div className="site-footer__container">
                    <div className="site-footer__top">
                        <div className="site-footer__brand">
                            <span className="site-footer__name">Kyle Street</span>
                            <span className="site-footer__tagline">
                                {t("homepage.footer.tagline")}
                            </span>
                        </div>
                        <nav className="site-footer__nav">
                            <a href="#services">{t("homepage.footer.navServices")}</a>
                            <a href="#contact">{t("homepage.footer.navContact")}</a>
                            <Link to="/client-portal">{t("homepage.footer.navPortal")}</Link>
                        </nav>
                    </div>
                    <div className="site-footer__bottom">
                        <p className="site-footer__copy">
                            &copy; {new Date().getFullYear()} Kyle Street. All rights reserved.
                        </p>
                        <p className="site-footer__legal">
                            EN &middot; FR &middot; 普通話
                        </p>
                    </div>
                </div>
            </footer>

        </div>
    );
};

export default HomePage;
